import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, MagnifyingGlass, FunnelSimple, CaretUp, CaretDown,
  Clock, CheckCircle, Warning, Bell, X, Sparkle, Trash, PencilSimple,
  ClockCounterClockwise, Eye, CaretRight, UploadSimple, Files, Tag,
  Scan, ArrowsClockwise, DownloadSimple, Link as LinkIcon, Buildings,
  CheckSquare, Square, CalendarBlank, Gear, FilePdf
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';

// Status configurations
const STATUS_CONFIG = {
  'Draft': { color: '#5E5E5E', bg: '#2D2D2D', border: '#3D3D3D' },
  'Consultation Open': { color: '#3B82F6', bg: '#1E3A5F', border: '#2563EB' },
  'Under Review': { color: '#F5A623', bg: '#3E2A0A', border: '#634310' },
  'Approved (Not Yet Effective)': { color: '#8B5CF6', bg: '#2D2560', border: '#6D28D9' },
  'Promulgated': { color: '#30A46C', bg: '#0F2E1B', border: '#174529' },
  'Open for Comment': { color: '#3B82F6', bg: '#1E3A5F', border: '#2563EB' },
  'Awaiting Promulgation': { color: '#8B5CF6', bg: '#2D2560', border: '#6D28D9' },
  'Enacted': { color: '#30A46C', bg: '#0F2E1B', border: '#174529' }
};

const READINESS_CONFIG = {
  'Not Started': { color: '#5E5E5E', icon: <Clock size={16} /> },
  'In Progress': { color: '#F5A623', icon: <ClockCounterClockwise size={16} /> },
  'Ready': { color: '#30A46C', icon: <CheckCircle size={16} /> }
};

const DOCUMENT_TYPES = [
  'Policy Draft',
  'Working Paper',
  'Presentation',
  'Risk Assessment',
  'Training Material',
  'Gap Analysis',
  'Implementation Plan'
];

const THEMES = [
  'Cybersecurity',
  'Data Protection',
  'Financial Crime',
  'Tax Compliance',
  'Corporate Governance',
  'Consumer Protection',
  'Employment & Labour'
];

const REGULATORY_SOURCES = [
  { id: 'Parliament', label: 'Parliament', description: 'Bills and Acts' },
  { id: 'SARB', label: 'SARB', description: 'South African Reserve Bank' },
  { id: 'FSCA', label: 'FSCA', description: 'Financial Sector Conduct Authority' },
  { id: 'Information Regulator', label: 'Information Regulator', description: 'POPIA enforcement' },
  { id: 'FIC', label: 'FIC', description: 'Financial Intelligence Centre' },
  { id: 'NCR', label: 'NCR', description: 'National Credit Regulator' }
];

// Regulators for Library filter
const REGULATORS = [
  'Parliament',
  'SARB',
  'FSCA',
  'FIC',
  'Information Regulator',
  'NCR',
  'CIPC',
  'DHA',
  'SARS',
  'DoL'
];

// Document Types for Library filter
const LIBRARY_DOC_TYPES = [
  'Act',
  'Bill',
  'Regulations',
  'Directive',
  'Circular',
  'Guidance Note',
  'Conduct Standard',
  'PCC',
  'Notice',
  'Code of Conduct',
  'Amendment'
];

// Components
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG['Draft'];
  return (
    <span 
      className="px-2.5 py-1 rounded-sm text-xs font-medium border"
      style={{ 
        color: config.color, 
        backgroundColor: config.bg, 
        borderColor: config.border 
      }}
    >
      {status}
    </span>
  );
};

const ReadinessBadge = ({ status }) => {
  const config = READINESS_CONFIG[status] || READINESS_CONFIG['Not Started'];
  return (
    <div 
      className="flex items-center gap-1.5 text-xs font-medium"
      style={{ color: config.color }}
    >
      {config.icon}
      <span>{status}</span>
    </div>
  );
};

const RiskScoreBadge = ({ impact, probability }) => {
  const score = impact * probability;
  let level = 'Low';
  let colors = { text: '#30A46C', bg: '#0F2E1B', border: '#174529' };
  
  if (score >= 15) {
    level = 'High';
    colors = { text: '#E5484D', bg: '#3A1214', border: '#5C1D1F' };
  } else if (score >= 8) {
    level = 'Medium';
    colors = { text: '#F5A623', bg: '#3E2A0A', border: '#634310' };
  }
  
  return (
    <div className="flex items-center gap-2">
      <span 
        className="px-2 py-0.5 rounded-sm text-xs font-mono font-semibold border"
        style={{ color: colors.text, backgroundColor: colors.bg, borderColor: colors.border }}
      >
        {score}
      </span>
      <span className="text-xs text-text-tertiary">{level}</span>
    </div>
  );
};

// Scan Draft Laws Modal
const ScanDraftLawsModal = ({ isOpen, onClose, onImport }) => {
  const [selectedThemes, setSelectedThemes] = useState(['Cybersecurity', 'Data Protection']);
  const [selectedSources, setSelectedSources] = useState(['Parliament', 'FSCA', 'SARB']);
  const [scanning, setScanning] = useState(false);
  const [scanningReal, setScanningReal] = useState(false);
  const [scannedDocs, setScannedDocs] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [scanSourcesInfo, setScanSourcesInfo] = useState(null);

  const toggleTheme = (theme) => {
    if (selectedThemes.includes(theme)) {
      setSelectedThemes(selectedThemes.filter(t => t !== theme));
    } else {
      setSelectedThemes([...selectedThemes, theme]);
    }
  };

  const toggleSource = (source) => {
    if (selectedSources.includes(source)) {
      setSelectedSources(selectedSources.filter(s => s !== source));
    } else {
      setSelectedSources([...selectedSources, source]);
    }
  };

  const toggleDocSelection = (docId) => {
    if (selectedDocs.includes(docId)) {
      setSelectedDocs(selectedDocs.filter(id => id !== docId));
    } else {
      setSelectedDocs([...selectedDocs, docId]);
    }
  };

  const selectAllDocs = () => {
    if (selectedDocs.length === scannedDocs.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(scannedDocs.map(d => d.id));
    }
  };

  const handleScan = async () => {
    if (selectedThemes.length === 0) {
      toast.error('Please select at least one theme');
      return;
    }
    if (selectedSources.length === 0) {
      toast.error('Please select at least one source');
      return;
    }

    setScanning(true);
    setScannedDocs([]);
    setSelectedDocs([]);

    try {
      const response = await axios.post(`${API}/draft-laws/scan`, {
        themes: selectedThemes,
        sources: selectedSources
      });
      
      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        setScannedDocs(response.data.documents || []);
        toast.success(`Found ${response.data.count} regulatory documents`);
      }
    } catch (error) {
      toast.error('Failed to scan for draft laws');
    } finally {
      setScanning(false);
    }
  };

  // Scan REAL Financial Crime sources (Parliament + FIC)
  const handleScanRealSources = async () => {
    setScanningReal(true);
    setScannedDocs([]);
    setSelectedDocs([]);
    setScanSourcesInfo(null);

    try {
      const response = await axios.post(`${API}/draft-laws/scan-financial-crime`, {
        include_all_parliament_bills: false  // Only financial-crime related
      });
      
      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        setScannedDocs(response.data.documents || []);
        setScanSourcesInfo(response.data.sources_scraped || []);
        toast.success(`Found ${response.data.count} documents from official sources`);
      }
    } catch (error) {
      toast.error('Failed to scan financial crime sources');
    } finally {
      setScanningReal(false);
    }
  };

  // Scan ALL regulators (comprehensive scan)
  const [scanningAll, setScanningAll] = useState(false);
  
  const handleScanAllRegulators = async () => {
    setScanningAll(true);
    setScannedDocs([]);
    setSelectedDocs([]);
    setScanSourcesInfo(null);

    try {
      const response = await axios.get(`${API}/draft-laws/scan-all-regulators`);
      
      if (response.data.error) {
        toast.error(response.data.error);
      } else {
        setScannedDocs(response.data.documents || []);
        setScanSourcesInfo(response.data.sources_scraped || []);
        toast.success(`Found ${response.data.count} documents across all regulators`);
      }
    } catch (error) {
      toast.error('Failed to scan all regulators');
    } finally {
      setScanningAll(false);
    }
  };

  const handleImport = async () => {
    if (selectedDocs.length === 0) {
      toast.error('Please select documents to import');
      return;
    }

    setImporting(true);
    try {
      const docsToImport = scannedDocs.filter(d => selectedDocs.includes(d.id));
      const response = await axios.post(`${API}/draft-laws/import-scanned`, docsToImport);
      toast.success(response.data.message);
      onImport();
      onClose();
      setScannedDocs([]);
      setSelectedDocs([]);
    } catch (error) {
      toast.error('Failed to import documents');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setScannedDocs([]);
    setSelectedDocs([]);
    setScanSourcesInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-lg font-heading font-medium text-text-primary flex items-center gap-2">
                <Scan size={20} className="text-gold" />
                Scan for Regulatory Documents
              </h2>
              <p className="text-sm text-text-secondary mt-1">
                Search Parliament and regulatory bodies for draft laws, circulars, and guidance notes
              </p>
            </div>
            <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Scan Configuration */}
            {scannedDocs.length === 0 && (
              <div className="p-6 space-y-6">
                {/* Theme Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-3">
                    Select Regulatory Themes
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {THEMES.map((theme) => (
                      <button
                        key={theme}
                        onClick={() => toggleTheme(theme)}
                        className={`px-3 py-2 rounded-sm text-sm font-medium transition-all ${
                          selectedThemes.includes(theme)
                            ? 'bg-gold text-gold-foreground'
                            : 'bg-background border border-border text-text-primary hover:border-gold'
                        }`}
                        data-testid={`scan-theme-${theme.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {theme}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Source Selection */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-3">
                    Select Regulatory Sources
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {REGULATORY_SOURCES.map((source) => (
                      <button
                        key={source.id}
                        onClick={() => toggleSource(source.id)}
                        className={`p-3 rounded-sm text-left transition-all ${
                          selectedSources.includes(source.id)
                            ? 'bg-gold/10 border-2 border-gold'
                            : 'bg-background border border-border hover:border-gold/50'
                        }`}
                        data-testid={`scan-source-${source.id.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Buildings size={16} className={selectedSources.includes(source.id) ? 'text-gold' : 'text-text-tertiary'} />
                          <span className={`text-sm font-medium ${selectedSources.includes(source.id) ? 'text-gold' : 'text-text-primary'}`}>
                            {source.label}
                          </span>
                        </div>
                        <p className="text-xs text-text-tertiary">{source.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Scan Button */}
                <div className="pt-4">
                  <button
                    onClick={handleScan}
                    disabled={scanning || scanningReal || selectedThemes.length === 0 || selectedSources.length === 0}
                    className="w-full px-4 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="scan-submit-btn"
                  >
                    {scanning ? (
                      <>
                        <ArrowsClockwise size={18} className="animate-spin" />
                        Scanning regulatory sources...
                      </>
                    ) : (
                      <>
                        <Scan size={18} />
                        Scan for Draft Laws (AI-Generated)
                      </>
                    )}
                  </button>
                  
                  {/* Divider */}
                  <div className="flex items-center gap-4 my-4">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-text-tertiary">OR</span>
                    <div className="flex-1 h-px bg-border" />
                  </div>

                  {/* Scan Real Sources Button */}
                  <button
                    onClick={handleScanRealSources}
                    disabled={scanning || scanningReal}
                    className="w-full px-4 py-3 bg-risk-low-bg hover:bg-risk-low-bg/80 text-risk-low-text border border-risk-low-border rounded-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="scan-real-sources-btn"
                  >
                    {scanningReal ? (
                      <>
                        <ArrowsClockwise size={18} className="animate-spin" />
                        Scraping official sources...
                      </>
                    ) : (
                      <>
                        <LinkIcon size={18} />
                        Scan REAL Financial Crime Sources
                      </>
                    )}
                  </button>
                  <p className="text-xs text-text-tertiary mt-2 text-center">
                    Scrapes real data from Parliament (parliament.gov.za) and FIC (fic.gov.za)
                  </p>
                  
                  {/* Scan ALL Regulators Button */}
                  <button
                    onClick={handleScanAllRegulators}
                    disabled={scanning || scanningReal || scanningAll}
                    className="w-full mt-3 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="scan-all-regulators-btn"
                  >
                    {scanningAll ? (
                      <>
                        <ArrowsClockwise size={18} className="animate-spin" />
                        Scanning all regulators...
                      </>
                    ) : (
                      <>
                        <Buildings size={18} />
                        Scan ALL Regulators (Comprehensive)
                      </>
                    )}
                  </button>
                  <p className="text-xs text-text-tertiary mt-2 text-center">
                    Parliament + FIC + SARB + Information Regulator
                  </p>
                </div>
              </div>
            )}

            {/* Sources Info */}
            {scanSourcesInfo && scanSourcesInfo.length > 0 && (
              <div className="px-6 pb-4">
                <div className="bg-background p-3 rounded-sm border border-border">
                  <h4 className="text-xs font-semibold text-text-primary mb-2">Sources Scraped:</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {scanSourcesInfo.map((source, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gold hover:underline truncate flex-1"
                        >
                          {source.source}
                        </a>
                        <span className="text-text-tertiary ml-2">{source.documents_found} docs</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Scan Results */}
            {scannedDocs.length > 0 && (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-medium text-text-primary">
                      Found {scannedDocs.length} Regulatory Documents
                    </h3>
                    <p className="text-xs text-text-tertiary mt-1">
                      Select documents to import to your tracker
                    </p>
                  </div>
                  <button
                    onClick={selectAllDocs}
                    className="flex items-center gap-2 text-sm text-gold hover:underline"
                  >
                    {selectedDocs.length === scannedDocs.length ? (
                      <>
                        <CheckSquare size={16} />
                        Deselect All
                      </>
                    ) : (
                      <>
                        <Square size={16} />
                        Select All
                      </>
                    )}
                  </button>
                </div>

                {/* Results Table */}
                <div className="border border-border rounded-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-background">
                      <tr>
                        <th className="px-3 py-3 text-left w-10">
                          <span className="sr-only">Select</span>
                        </th>
                        <th className="px-3 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Document
                        </th>
                        <th className="px-3 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold w-28">
                          Issue Date
                        </th>
                        <th className="px-3 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold w-36">
                          Regulator
                        </th>
                        <th className="px-3 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold w-32">
                          Status
                        </th>
                        <th className="px-3 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold w-20">
                          Link
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {scannedDocs.map((doc, index) => (
                        <tr 
                          key={doc.id}
                          className={`transition-colors cursor-pointer ${
                            selectedDocs.includes(doc.id) 
                              ? 'bg-gold/5' 
                              : 'hover:bg-background-hover'
                          }`}
                          onClick={() => toggleDocSelection(doc.id)}
                          data-testid={`scanned-doc-row-${index}`}
                        >
                          <td className="px-3 py-3">
                            <div className={`w-5 h-5 rounded-sm border flex items-center justify-center ${
                              selectedDocs.includes(doc.id) 
                                ? 'bg-gold border-gold' 
                                : 'border-border'
                            }`}>
                              {selectedDocs.includes(doc.id) && (
                                <CheckCircle size={14} className="text-gold-foreground" weight="bold" />
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div>
                              <div className="text-sm font-medium text-text-primary">
                                {doc.document_name}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="px-1.5 py-0.5 bg-background rounded text-xs text-text-tertiary">
                                  {doc.document_type}
                                </span>
                                <span className="px-1.5 py-0.5 bg-gold/10 rounded text-xs text-gold">
                                  {doc.theme}
                                </span>
                              </div>
                              {doc.summary && (
                                <p className="text-xs text-text-secondary mt-1 line-clamp-2">
                                  {doc.summary}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 text-sm text-text-secondary">
                            {doc.issue_date ? new Date(doc.issue_date).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-sm text-text-primary">{doc.regulator}</span>
                          </td>
                          <td className="px-3 py-3">
                            <StatusBadge status={doc.status} />
                          </td>
                          <td className="px-3 py-3 text-center">
                            {doc.download_url && (
                              <a
                                href={doc.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center justify-center w-8 h-8 text-text-tertiary hover:text-gold transition-colors"
                              >
                                <DownloadSimple size={16} />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          {scannedDocs.length > 0 && (
            <div className="p-4 border-t border-border bg-background flex items-center justify-between">
              <button
                onClick={() => {
                  setScannedDocs([]);
                  setSelectedDocs([]);
                }}
                className="px-4 py-2 bg-background-surface border border-border text-text-primary rounded-sm hover:border-gold transition-all"
              >
                Scan Again
              </button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-text-secondary">
                  {selectedDocs.length} of {scannedDocs.length} selected
                </span>
                <button
                  onClick={handleImport}
                  disabled={importing || selectedDocs.length === 0}
                  className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50 flex items-center gap-2"
                  data-testid="import-scanned-btn"
                >
                  {importing ? (
                    <>
                      <ArrowsClockwise size={16} className="animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <UploadSimple size={16} />
                      Import Selected
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Excel Upload Modal
const ExcelUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx') || droppedFile.name.endsWith('.xls')) {
        setFile(droppedFile);
        setUploadResult(null);
      } else {
        toast.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/library/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} documents`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload file';
      toast.error(errorMsg);
      setUploadResult({ success: false, message: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/library/template`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'regulatory_library_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleClose = () => {
    setFile(null);
    setUploadResult(null);
    setDragActive(false);
    if (uploadResult?.imported_count > 0) {
      onUploadComplete();
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-lg max-h-[80vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-lg font-heading font-medium text-text-primary">
                Import from Excel
              </h2>
              <p className="text-xs text-text-tertiary mt-1">
                Upload your curated regulatory library
              </p>
            </div>
            <button onClick={handleClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(80vh-180px)]">
            {/* Download Template */}
            <div className="flex items-center justify-between p-3 bg-background rounded-sm border border-border">
              <div>
                <p className="text-sm text-text-primary">Need a template?</p>
                <p className="text-xs text-text-tertiary">Download our Excel template with sample data</p>
              </div>
              <button
                onClick={handleDownloadTemplate}
                className="px-3 py-1.5 bg-gold/10 text-gold text-sm rounded-sm hover:bg-gold/20 transition-all flex items-center gap-2"
              >
                <DownloadSimple size={16} />
                Template
              </button>
            </div>

            {/* Drop Zone */}
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`
                border-2 border-dashed rounded-sm p-8 text-center transition-all cursor-pointer
                ${dragActive 
                  ? 'border-gold bg-gold/5' 
                  : file 
                    ? 'border-risk-low-border bg-risk-low-bg/30' 
                    : 'border-border hover:border-gold/50'
                }
              `}
              onClick={() => document.getElementById('excel-file-input').click()}
            >
              <input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {file ? (
                <div className="space-y-2">
                  <Files size={40} className="mx-auto text-risk-low-text" />
                  <p className="text-sm font-medium text-text-primary">{file.name}</p>
                  <p className="text-xs text-text-tertiary">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                      setUploadResult(null);
                    }}
                    className="text-xs text-risk-high-text hover:underline"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <UploadSimple size={40} className="mx-auto text-text-tertiary" />
                  <p className="text-sm text-text-primary">
                    Drag & drop your Excel file here
                  </p>
                  <p className="text-xs text-text-tertiary">
                    or click to browse (.xlsx, .xls)
                  </p>
                </div>
              )}
            </div>

            {/* Upload Result */}
            {uploadResult && (
              <div className={`p-4 rounded-sm border ${
                uploadResult.success 
                  ? 'bg-risk-low-bg/30 border-risk-low-border' 
                  : 'bg-risk-high-bg/30 border-risk-high-border'
              }`}>
                <div className="flex items-start gap-3">
                  {uploadResult.success ? (
                    <CheckCircle size={20} className="text-risk-low-text flex-shrink-0 mt-0.5" />
                  ) : (
                    <Warning size={20} className="text-risk-high-text flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${
                      uploadResult.success ? 'text-risk-low-text' : 'text-risk-high-text'
                    }`}>
                      {uploadResult.message}
                    </p>
                    
                    {uploadResult.imported_documents && uploadResult.imported_documents.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-text-tertiary mb-2">Imported documents:</p>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {uploadResult.imported_documents.map((doc, idx) => (
                            <div key={idx} className="text-xs text-text-secondary flex items-center gap-2">
                              <FileText size={12} className="text-gold" />
                              <span className="truncate">{doc.document_name}</span>
                              <span className="text-text-tertiary">({doc.status})</span>
                            </div>
                          ))}
                        </div>
                        {uploadResult.imported_count > 10 && (
                          <p className="text-xs text-text-tertiary mt-2">
                            ...and {uploadResult.imported_count - 10} more
                          </p>
                        )}
                      </div>
                    )}

                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs text-risk-high-text mb-2">
                          {uploadResult.total_errors} error(s):
                        </p>
                        <div className="space-y-1 max-h-24 overflow-y-auto">
                          {uploadResult.errors.slice(0, 5).map((error, idx) => (
                            <p key={idx} className="text-xs text-text-tertiary">{error}</p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Expected Columns Info */}
            <div className="text-xs text-text-tertiary">
              <p className="font-medium text-text-secondary mb-1">Required columns:</p>
              <p>document_name, document_type, regulator, theme, status</p>
              <p className="mt-1">Optional: issue_date, effective_date, summary, download_url</p>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-border flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
            >
              {uploadResult?.imported_count > 0 ? 'Done' : 'Cancel'}
            </button>
            {!uploadResult?.success && (
              <button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50 flex items-center gap-2"
              >
                {uploading ? (
                  <>
                    <ArrowsClockwise size={16} className="animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <UploadSimple size={16} />
                    Import Documents
                  </>
                )}
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Add Draft Law Modal - Enhanced with all regulatory fields
const AddDraftLawModal = ({ isOpen, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    document_name: '',
    document_type: 'Bill',
    issued_by: '',
    status: 'Draft',
    theme: 'Cybersecurity',
    publication_date: '',
    deadline: '',
    effective_date: '',
    overview: '',
    source_url: '',
    download_url: '',
    possible_impact: 3,
    probability: 3
  });
  const [loading, setLoading] = useState(false);

  const DOCUMENT_TYPE_OPTIONS = [
    'Act',
    'Bill',
    'Regulations',
    'Directive',
    'Circular',
    'Guidance Note',
    'Conduct Standard',
    'Public Compliance Communication (PCC)',
    'Practice Note',
    'Amendment',
    'Notice',
    'Code of Conduct'
  ];

  const REGULATOR_OPTIONS = [
    'Parliament',
    'SARB',
    'FSCA',
    'Information Regulator',
    'FIC',
    'NCR',
    'CIPC',
    'Council for Medical Schemes',
    'Competition Commission'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.document_name || !formData.issued_by || !formData.theme) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/draft-laws`, formData);
      toast.success('Document added to library');
      onAdd(response.data);
      onClose();
      setFormData({
        document_name: '',
        document_type: 'Bill',
        issued_by: '',
        status: 'Draft',
        theme: 'Cybersecurity',
        publication_date: '',
        deadline: '',
        effective_date: '',
        overview: '',
        source_url: '',
        download_url: '',
        possible_impact: 3,
        probability: 3
      });
    } catch (error) {
      toast.error('Failed to add draft law');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background-surface">
            <div>
              <h2 className="text-lg font-heading font-medium text-text-primary">
                Add Document to Library
              </h2>
              <p className="text-xs text-text-tertiary mt-1">
                Add a draft or promulgated regulatory document
              </p>
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Document Name */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Document Name <span className="text-risk-high-text">*</span>
              </label>
              <input
                type="text"
                value={formData.document_name}
                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="e.g., Draft Data Protection Amendment Bill, 2025"
                data-testid="draft-law-name-input"
              />
            </div>

            {/* Document Type & Regulatory Theme */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  Document Type <span className="text-risk-high-text">*</span>
                </label>
                <select
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="draft-law-type-select"
                >
                  {DOCUMENT_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  Regulatory Theme <span className="text-risk-high-text">*</span>
                </label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="draft-law-theme-select"
                >
                  {THEMES.map((theme) => (
                    <option key={theme} value={theme}>{theme}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Issued By & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  Issued By / Regulator <span className="text-risk-high-text">*</span>
                </label>
                <select
                  value={formData.issued_by}
                  onChange={(e) => setFormData({ ...formData, issued_by: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="draft-law-issuer-select"
                >
                  <option value="">Select Regulator</option>
                  {REGULATOR_OPTIONS.map((reg) => (
                    <option key={reg} value={reg}>{reg}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="draft-law-status-select"
                >
                  {Object.keys(STATUS_CONFIG).filter(s => !['Open for Comment', 'Awaiting Promulgation', 'Enacted'].includes(s)).map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Dates Section */}
            <div className="p-4 bg-background border border-border rounded-sm">
              <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
                <CalendarBlank size={16} className="text-gold" />
                Key Dates
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-text-tertiary mb-2">
                    Publication Date
                  </label>
                  <input
                    type="date"
                    value={formData.publication_date}
                    onChange={(e) => setFormData({ ...formData, publication_date: e.target.value })}
                    className="w-full px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
                    data-testid="draft-law-pub-date"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-tertiary mb-2">
                    Comment Deadline
                  </label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                    className="w-full px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
                    data-testid="draft-law-deadline-input"
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-tertiary mb-2">
                    Effective Date
                  </label>
                  <input
                    type="date"
                    value={formData.effective_date}
                    onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    className="w-full px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
                    data-testid="draft-law-effective-date"
                  />
                </div>
              </div>
            </div>

            {/* Overview/Summary */}
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Overview / Summary
              </label>
              <textarea
                value={formData.overview}
                onChange={(e) => setFormData({ ...formData, overview: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none resize-none"
                rows={3}
                placeholder="Brief description of the regulatory document and its key provisions..."
                data-testid="draft-law-overview-input"
              />
            </div>

            {/* Links Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  Source URL
                </label>
                <input
                  type="url"
                  value={formData.source_url}
                  onChange={(e) => setFormData({ ...formData, source_url: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  placeholder="https://www.gov.za/..."
                  data-testid="draft-law-source-url"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-2">
                  Download URL
                </label>
                <input
                  type="url"
                  value={formData.download_url}
                  onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  placeholder="https://..."
                  data-testid="draft-law-download-url"
                />
              </div>
            </div>

            {/* Risk Assessment */}
            <div className="p-4 bg-background border border-border rounded-sm">
              <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
                <Warning size={16} className="text-gold" />
                Risk Assessment
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-text-tertiary mb-2">
                    Impact (1-5)
                  </label>
                  <select
                    value={formData.possible_impact}
                    onChange={(e) => setFormData({ ...formData, possible_impact: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
                    data-testid="draft-law-impact"
                  >
                    {[1, 2, 3, 4, 5].map((val) => (
                      <option key={val} value={val}>{val} - {val <= 2 ? 'Low' : val <= 3 ? 'Medium' : 'High'}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-text-tertiary mb-2">
                    Probability (1-5)
                  </label>
                  <select
                    value={formData.probability}
                    onChange={(e) => setFormData({ ...formData, probability: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary text-sm focus:border-gold focus:outline-none"
                    data-testid="draft-law-probability"
                  >
                    {[1, 2, 3, 4, 5].map((val) => (
                      <option key={val} value={val}>{val} - {val <= 2 ? 'Low' : val <= 3 ? 'Medium' : 'High'}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3 text-center">
                <span className="text-xs text-text-tertiary">Risk Score: </span>
                <span className={`text-sm font-mono font-semibold ${
                  formData.possible_impact * formData.probability >= 15 
                    ? 'text-risk-high-text' 
                    : formData.possible_impact * formData.probability >= 8 
                      ? 'text-risk-medium-text' 
                      : 'text-risk-low-text'
                }`}>
                  {formData.possible_impact * formData.probability}
                </span>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50"
                data-testid="draft-law-submit-btn"
              >
                {loading ? 'Adding...' : 'Add Draft Law'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Scan Schedule Modal
const ScanScheduleModal = ({ isOpen, onClose, onSave }) => {
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: 'weekly',
    themes: ['Cybersecurity', 'Data Protection'],
    sources: ['Parliament', 'FSCA', 'SARB'],
    notify_on_new: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [scanResults, setScanResults] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchSchedule();
    }
  }, [isOpen]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/scan-schedule`);
      setSchedule(response.data);
    } catch (error) {
      toast.error('Failed to load scan schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/scan-schedule`, schedule);
      toast.success(schedule.enabled ? 'Automated scan enabled' : 'Scan schedule saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save scan schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    setRunningNow(true);
    setScanResults(null);
    try {
      const response = await axios.post(`${API}/scan-schedule/run-now`);
      setScanResults(response.data);
      if (response.data.count > 0) {
        toast.success(`Found ${response.data.count} new documents!`);
      } else {
        toast.info('No new documents found');
      }
    } catch (error) {
      toast.error('Failed to run scan');
    } finally {
      setRunningNow(false);
    }
  };

  const toggleTheme = (theme) => {
    if (schedule.themes.includes(theme)) {
      setSchedule({ ...schedule, themes: schedule.themes.filter(t => t !== theme) });
    } else {
      setSchedule({ ...schedule, themes: [...schedule.themes, theme] });
    }
  };

  const toggleSource = (source) => {
    if (schedule.sources.includes(source)) {
      setSchedule({ ...schedule, sources: schedule.sources.filter(s => s !== source) });
    } else {
      setSchedule({ ...schedule, sources: [...schedule.sources, source] });
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background-surface">
            <div>
              <h2 className="text-lg font-heading font-medium text-text-primary flex items-center gap-2">
                <Clock size={20} className="text-gold" />
                Automated Scan Schedule
              </h2>
              <p className="text-xs text-text-tertiary mt-1">
                Configure automatic scanning for new regulatory documents
              </p>
            </div>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>

          {loading ? (
            <div className="p-6 text-center text-text-tertiary">
              Loading schedule settings...
            </div>
          ) : (
            <div className="p-6 space-y-6">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-background border border-border rounded-sm">
                <div>
                  <h3 className="text-sm font-medium text-text-primary">
                    Enable Automated Scanning
                  </h3>
                  <p className="text-xs text-text-tertiary mt-1">
                    Automatically scan for new regulatory documents on a schedule
                  </p>
                </div>
                <button
                  onClick={() => setSchedule({ ...schedule, enabled: !schedule.enabled })}
                  className={`w-12 h-6 rounded-full transition-all ${
                    schedule.enabled ? 'bg-gold' : 'bg-border'
                  }`}
                  data-testid="schedule-enabled-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-all ${
                    schedule.enabled ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Frequency Selection */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Scan Frequency
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'bi-weekly', label: 'Bi-Weekly' },
                    { value: 'monthly', label: 'Monthly' }
                  ].map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setSchedule({ ...schedule, frequency: freq.value })}
                      className={`px-3 py-2 rounded-sm text-sm font-medium transition-all ${
                        schedule.frequency === freq.value
                          ? 'bg-gold text-gold-foreground'
                          : 'bg-background border border-border text-text-primary hover:border-gold'
                      }`}
                      data-testid={`frequency-${freq.value}`}
                    >
                      {freq.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme Selection */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Regulatory Themes to Monitor
                </label>
                <div className="flex flex-wrap gap-2">
                  {THEMES.map((theme) => (
                    <button
                      key={theme}
                      onClick={() => toggleTheme(theme)}
                      className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${
                        schedule.themes.includes(theme)
                          ? 'bg-gold text-gold-foreground'
                          : 'bg-background border border-border text-text-primary hover:border-gold'
                      }`}
                      data-testid={`schedule-theme-${theme.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
              </div>

              {/* Source Selection */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-3">
                  Regulatory Sources to Scan
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {REGULATORY_SOURCES.map((source) => (
                    <button
                      key={source.id}
                      onClick={() => toggleSource(source.id)}
                      className={`px-3 py-2 rounded-sm text-sm text-left transition-all ${
                        schedule.sources.includes(source.id)
                          ? 'bg-gold/10 border-2 border-gold text-gold'
                          : 'bg-background border border-border text-text-primary hover:border-gold/50'
                      }`}
                      data-testid={`schedule-source-${source.id.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {source.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Notifications Toggle */}
              <div className="flex items-center justify-between p-4 bg-background border border-border rounded-sm">
                <div>
                  <h3 className="text-sm font-medium text-text-primary flex items-center gap-2">
                    <Bell size={16} className="text-gold" />
                    Notify on New Documents
                  </h3>
                  <p className="text-xs text-text-tertiary mt-1">
                    Receive notifications when new documents are found
                  </p>
                </div>
                <button
                  onClick={() => setSchedule({ ...schedule, notify_on_new: !schedule.notify_on_new })}
                  className={`w-12 h-6 rounded-full transition-all ${
                    schedule.notify_on_new ? 'bg-gold' : 'bg-border'
                  }`}
                  data-testid="notify-toggle"
                >
                  <div className={`w-5 h-5 bg-white rounded-full transition-all ${
                    schedule.notify_on_new ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Schedule Info */}
              {schedule.last_scan && (
                <div className="p-4 bg-background border border-border rounded-sm">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-text-tertiary">Last Scan:</span>
                      <span className="text-text-primary ml-2">
                        {new Date(schedule.last_scan).toLocaleString()}
                      </span>
                    </div>
                    {schedule.next_scan && (
                      <div>
                        <span className="text-text-tertiary">Next Scan:</span>
                        <span className="text-gold ml-2">
                          {new Date(schedule.next_scan).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Scan Results */}
              {scanResults && (
                <div className="p-4 bg-gold/10 border border-gold rounded-sm">
                  <h3 className="text-sm font-medium text-gold mb-2">Scan Results</h3>
                  <div className="text-sm text-text-primary">
                    <p>Found <span className="font-semibold text-gold">{scanResults.count}</span> new documents</p>
                    <p className="text-xs text-text-tertiary mt-1">
                      Total scanned: {scanResults.total_scanned}, Duplicates filtered: {scanResults.duplicates_filtered}
                    </p>
                  </div>
                  {scanResults.documents && scanResults.documents.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {scanResults.documents.slice(0, 3).map((doc, idx) => (
                        <div key={idx} className="text-xs text-text-secondary">
                          • {doc.document_name}
                        </div>
                      ))}
                      {scanResults.documents.length > 3 && (
                        <div className="text-xs text-text-tertiary">
                          +{scanResults.documents.length - 3} more...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Footer Actions */}
          <div className="p-4 border-t border-border bg-background flex items-center justify-between">
            <button
              onClick={handleRunNow}
              disabled={runningNow || schedule.themes.length === 0 || schedule.sources.length === 0}
              className="px-4 py-2 bg-background-surface border border-gold text-gold rounded-sm hover:bg-gold/10 transition-all flex items-center gap-2 disabled:opacity-50"
              data-testid="run-scan-now-btn"
            >
              {runningNow ? (
                <>
                  <ArrowsClockwise size={16} className="animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Scan size={16} />
                  Run Scan Now
                </>
              )}
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50"
                data-testid="save-schedule-btn"
              >
                {saving ? 'Saving...' : 'Save Schedule'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Add Preparation Document Modal
const AddDocumentModal = ({ isOpen, onClose, onAdd, draftLaws }) => {
  const [formData, setFormData] = useState({
    document_name: '',
    document_type: 'Policy Draft',
    related_draft_law_id: '',
    theme: 'Cybersecurity',
    file_url: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.document_name) {
      toast.error('Please enter document name');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/preparation-documents`, formData);
      toast.success('Document added successfully');
      onAdd(response.data);
      onClose();
      setFormData({
        document_name: '',
        document_type: 'Policy Draft',
        related_draft_law_id: '',
        theme: 'Cybersecurity',
        file_url: ''
      });
    } catch (error) {
      toast.error('Failed to add document');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-lg mx-4"
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-lg font-heading font-medium text-text-primary">
              Add Preparation Document
            </h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Document Name <span className="text-risk-high-text">*</span>
              </label>
              <input
                type="text"
                value={formData.document_name}
                onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="e.g., POPIA Compliance Gap Analysis"
                data-testid="prep-doc-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-text-secondary mb-2">Document Type</label>
                <select
                  value={formData.document_type}
                  onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="prep-doc-type-select"
                >
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-text-secondary mb-2">Theme</label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                  className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                  data-testid="prep-doc-theme-select"
                >
                  {THEMES.map((theme) => (
                    <option key={theme} value={theme}>{theme}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                Related Draft Law (Optional)
              </label>
              <select
                value={formData.related_draft_law_id}
                onChange={(e) => setFormData({ ...formData, related_draft_law_id: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                data-testid="prep-doc-related-select"
              >
                <option value="">None</option>
                {draftLaws.map((law) => (
                  <option key={law.id} value={law.id}>{law.document_name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-text-secondary mb-2">
                File URL (Optional)
              </label>
              <input
                type="url"
                value={formData.file_url}
                onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
                className="w-full px-4 py-3 bg-background border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none"
                placeholder="https://..."
                data-testid="prep-doc-url-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all disabled:opacity-50"
                data-testid="prep-doc-submit-btn"
              >
                {loading ? 'Adding...' : 'Add Document'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Version History Modal
const VersionHistoryModal = ({ isOpen, onClose, draftLaw }) => {
  if (!isOpen || !draftLaw) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-background-surface border border-border rounded-sm w-full max-w-lg mx-4"
        >
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-lg font-heading font-medium text-text-primary">
              Version History
            </h2>
            <button onClick={onClose} className="text-text-tertiary hover:text-text-primary">
              <X size={20} />
            </button>
          </div>
          
          <div className="p-6">
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gold">{draftLaw.document_name}</h3>
              <p className="text-xs text-text-tertiary mt-1">Issued by {draftLaw.issued_by}</p>
            </div>
            
            {draftLaw.version_history && draftLaw.version_history.length > 0 ? (
              <div className="space-y-3">
                {draftLaw.version_history.map((version, idx) => (
                  <div 
                    key={idx}
                    className="p-4 bg-background border border-border rounded-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-text-primary">{version.change}</p>
                        <p className="text-xs text-text-tertiary mt-1">
                          {new Date(version.date).toLocaleDateString()} by {version.source}
                        </p>
                      </div>
                      <ClockCounterClockwise size={16} className="text-text-tertiary" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockCounterClockwise size={32} className="mx-auto mb-2 text-text-tertiary" />
                <p className="text-sm text-text-tertiary">No version history yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Main Page Component
const DraftLawsPage = () => {
  const { user } = useAuth();
  const [draftLaws, setDraftLaws] = useState([]);
  const [prepDocuments, setPrepDocuments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [themeFilter, setThemeFilter] = useState('all');
  const [docTypeFilter, setDocTypeFilter] = useState('all');
  const [regulatorFilter, setRegulatorFilter] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [syncing, setSyncing] = useState(false);
  
  // Modals
  const [showAddLawModal, setShowAddLawModal] = useState(false);
  const [showAddDocModal, setShowAddDocModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState(null);
  
  // Super Admin check
  const isSuperAdmin = user?.is_super_admin;
  
  // Active tab
  const [activeTab, setActiveTab] = useState('tracker');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [lawsRes, docsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/draft-laws`),
        axios.get(`${API}/preparation-documents`),
        axios.get(`${API}/draft-law-notifications`)
      ]);
      setDraftLaws(lawsRes.data);
      setPrepDocuments(docsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeBatch = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/draft-laws/analyze-batch`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to analyze draft laws');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDeleteLaw = async (id) => {
    if (!window.confirm('Are you sure you want to delete this draft law?')) return;
    try {
      await axios.delete(`${API}/draft-laws/${id}`);
      setDraftLaws(draftLaws.filter(l => l.id !== id));
      toast.success('Draft law deleted');
    } catch (error) {
      toast.error('Failed to delete draft law');
    }
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await axios.delete(`${API}/preparation-documents/${id}`);
      setPrepDocuments(prepDocuments.filter(d => d.id !== id));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleAcknowledgeNotification = async (id) => {
    try {
      await axios.put(`${API}/draft-law-notifications/${id}/acknowledge`);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, acknowledged: true } : n
      ));
    } catch (error) {
      toast.error('Failed to acknowledge notification');
    }
  };

  // Sync from Master Library (Super Admin only)
  const handleSyncFromMasterLibrary = async () => {
    if (!isSuperAdmin) {
      toast.error('Super Admin access required');
      return;
    }
    
    setSyncing(true);
    try {
      toast.loading('Syncing from Master Library...', { id: 'master-sync' });
      
      const response = await axios.post(`${API}/master-library/sync-to-library`);
      
      const { stats } = response.data;
      toast.success(
        `Sync complete! ${stats.new_entries_created} new entries created, ${stats.pdfs_linked_to_existing} PDFs linked to existing`,
        { id: 'master-sync', duration: 5000 }
      );
      
      // Refresh data
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Super Admin access required', { id: 'master-sync' });
      } else {
        toast.error(error.response?.data?.detail || 'Failed to sync from Master Library', { id: 'master-sync' });
      }
    } finally {
      setSyncing(false);
    }
  };

  // PDF Upload handler
  const handleUploadPdf = async (documentId, file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      toast.loading('Uploading PDF...', { id: 'pdf-upload' });
      
      const response = await axios.post(
        `${API}/library/${documentId}/upload-pdf`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success(`PDF uploaded: ${response.data.filename}`, { id: 'pdf-upload' });
      
      // Refresh data to show updated PDF info
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload PDF';
      toast.error(errorMsg, { id: 'pdf-upload' });
    }
  };

  // PDF Download handler
  // Open PDF in new window for preview (with download option in browser)
  const handleViewPdf = (documentId, filename) => {
    // Open the PDF in a new tab - browser's built-in PDF viewer allows download
    const pdfUrl = `${API}/library/${documentId}/view-pdf`;
    window.open(pdfUrl, '_blank');
  };

  // Filtered and sorted draft laws
  const filteredLaws = useMemo(() => {
    let result = [...draftLaws];
    
    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(law => 
        law.document_name.toLowerCase().includes(q) ||
        law.issued_by.toLowerCase().includes(q)
      );
    }
    
    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(law => law.status === statusFilter);
    }
    
    // Theme filter
    if (themeFilter !== 'all') {
      result = result.filter(law => law.theme === themeFilter);
    }
    
    // Document Type filter
    if (docTypeFilter !== 'all') {
      result = result.filter(law => law.document_type === docTypeFilter);
    }
    
    // Regulator filter
    if (regulatorFilter !== 'all') {
      result = result.filter(law => law.issued_by === regulatorFilter);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'risk_score') {
        aVal = a.possible_impact * a.probability;
        bVal = b.possible_impact * b.probability;
      }
      
      if (sortField === 'issue_date') {
        aVal = a.publication_date ? new Date(a.publication_date).getTime() : 0;
        bVal = b.publication_date ? new Date(b.publication_date).getTime() : 0;
      }
      
      if (sortDir === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
    
    return result;
  }, [draftLaws, searchQuery, statusFilter, themeFilter, docTypeFilter, regulatorFilter, sortField, sortDir]);

  // Stats
  const stats = useMemo(() => {
    const total = draftLaws.length;
    const consultationOpen = draftLaws.filter(l => l.status === 'Consultation Open').length;
    const promulgated = draftLaws.filter(l => l.status === 'Promulgated').length;
    const highRisk = draftLaws.filter(l => l.possible_impact * l.probability >= 15).length;
    return { total, consultationOpen, promulgated, highRisk };
  }, [draftLaws]);

  const unacknowledgedNotifications = notifications.filter(n => !n.acknowledged);

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <div className="ml-64 flex-1 flex items-center justify-center">
          <div className="text-gold font-mono">Loading draft laws...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="ml-64 flex-1">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-heading font-medium text-text-primary mb-1">
                Regulatory Library
              </h1>
              <p className="text-sm text-text-secondary">
                Curated collection of draft and promulgated laws, regulations, and guidance notes
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowUploadModal(true)}
                className="px-4 py-2 bg-background-surface border border-border text-text-primary rounded-sm hover:border-gold transition-all flex items-center gap-2"
                data-testid="upload-excel-btn"
              >
                <UploadSimple size={16} className="text-gold" />
                Import Excel
              </button>
              {/* Sync from Master Library - Super Admin Only */}
              {isSuperAdmin && (
                <button
                  onClick={handleSyncFromMasterLibrary}
                  disabled={syncing}
                  className="px-4 py-2 bg-gold/20 border border-gold text-gold rounded-sm hover:bg-gold/30 transition-all flex items-center gap-2 disabled:opacity-50"
                  data-testid="sync-master-library-btn"
                  title="Sync documents from Master Library"
                >
                  {syncing ? (
                    <ArrowsClockwise size={16} className="animate-spin" />
                  ) : (
                    <LinkIcon size={16} />
                  )}
                  {syncing ? 'Syncing...' : 'Sync Master Library'}
                </button>
              )}
              <button
                onClick={() => setShowAddLawModal(true)}
                className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2"
                data-testid="add-document-btn"
              >
                <Plus size={16} />
                Add Document
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-gold">{stats.total}</div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Documents in Library
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold" style={{ color: '#3B82F6' }}>
                {stats.consultationOpen}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Consultation Open
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-4 bg-background-surface border border-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-risk-low-text">
                {stats.promulgated}
              </div>
              <div className="text-xs uppercase tracking-wider text-text-tertiary mt-1">
                Promulgated
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-risk-high-bg border border-risk-high-border rounded-sm"
            >
              <div className="text-2xl font-mono font-semibold text-risk-high-text">
                {stats.highRisk}
              </div>
              <div className="text-xs uppercase tracking-wider text-risk-high-text opacity-80 mt-1">
                High Risk
              </div>
            </motion.div>
          </div>
        </div>

        {/* Notifications Banner */}
        {unacknowledgedNotifications.length > 0 && (
          <div className="mx-6 mt-4">
            <div className="p-4 bg-gold/10 border border-gold rounded-sm">
              <div className="flex items-center gap-3">
                <Bell size={20} className="text-gold" weight="fill" />
                <div className="flex-1">
                  <span className="text-sm font-medium text-gold">
                    {unacknowledgedNotifications.length} new notification{unacknowledgedNotifications.length > 1 ? 's' : ''}
                  </span>
                  <span className="text-sm text-text-secondary ml-2">
                    - {unacknowledgedNotifications[0]?.message}
                  </span>
                </div>
                <button
                  onClick={() => handleAcknowledgeNotification(unacknowledgedNotifications[0]?.id)}
                  className="text-xs text-gold hover:underline"
                >
                  Acknowledge
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="px-6 pt-6">
          <div className="flex gap-1 border-b border-border">
            <button
              onClick={() => setActiveTab('tracker')}
              className={`px-4 py-2.5 text-sm font-medium transition-all ${
                activeTab === 'tracker' 
                  ? 'text-gold border-b-2 border-gold' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="tab-tracker"
            >
              <div className="flex items-center gap-2">
                <FileText size={16} />
                Library Tracker
              </div>
            </button>
            <button
              onClick={() => setActiveTab('workspace')}
              className={`px-4 py-2.5 text-sm font-medium transition-all ${
                activeTab === 'workspace' 
                  ? 'text-gold border-b-2 border-gold' 
                  : 'text-text-secondary hover:text-text-primary'
              }`}
              data-testid="tab-workspace"
            >
              <div className="flex items-center gap-2">
                <Files size={16} />
                Preparation Workspace
              </div>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'tracker' ? (
            <>
              {/* Filters */}
              <div className="flex flex-col gap-4 mb-6">
                {/* Search Row */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search documents..."
                      className="w-full pl-10 pr-4 py-2.5 bg-background-surface border border-border rounded-sm text-text-primary placeholder:text-text-tertiary focus:border-gold focus:outline-none"
                      data-testid="search-draft-laws"
                    />
                  </div>
                </div>
                
                {/* Filter Row */}
                <div className="flex items-center gap-3 flex-wrap">
                  <FunnelSimple size={18} className="text-text-tertiary" />
                  
                  {/* Document Type Filter */}
                  <select
                    value={docTypeFilter}
                    onChange={(e) => setDocTypeFilter(e.target.value)}
                    className="px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                    data-testid="filter-doc-type"
                  >
                    <option value="all">All Document Types</option>
                    {LIBRARY_DOC_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  
                  {/* Regulator Filter */}
                  <select
                    value={regulatorFilter}
                    onChange={(e) => setRegulatorFilter(e.target.value)}
                    className="px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                    data-testid="filter-regulator"
                  >
                    <option value="all">All Regulators</option>
                    {REGULATORS.map(reg => (
                      <option key={reg} value={reg}>{reg}</option>
                    ))}
                  </select>
                  
                  {/* Theme Filter */}
                  <select
                    value={themeFilter}
                    onChange={(e) => setThemeFilter(e.target.value)}
                    className="px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                    data-testid="filter-theme"
                  >
                    <option value="all">All Themes</option>
                    {THEMES.map(theme => (
                      <option key={theme} value={theme}>{theme}</option>
                    ))}
                  </select>
                  
                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-background-surface border border-border rounded-sm text-text-primary focus:border-gold focus:outline-none text-sm"
                    data-testid="filter-status"
                  >
                    <option value="all">All Status</option>
                    {Object.keys(STATUS_CONFIG).filter(s => !['Open for Comment', 'Awaiting Promulgation', 'Enacted'].includes(s)).map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                  
                  {/* Clear Filters */}
                  {(docTypeFilter !== 'all' || regulatorFilter !== 'all' || themeFilter !== 'all' || statusFilter !== 'all' || searchQuery) && (
                    <button
                      onClick={() => {
                        setDocTypeFilter('all');
                        setRegulatorFilter('all');
                        setThemeFilter('all');
                        setStatusFilter('all');
                        setSearchQuery('');
                      }}
                      className="px-3 py-2 text-sm text-gold hover:text-gold-hover transition-colors"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* Draft Laws Table */}
              {filteredLaws.length > 0 ? (
                <div className="bg-background-surface border border-border rounded-sm overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-background">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Document Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Document Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Regulator
                        </th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Theme
                        </th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Status
                        </th>
                        <th 
                          className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold cursor-pointer hover:text-gold"
                          onClick={() => {
                            if (sortField === 'issue_date') {
                              setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSortField('issue_date');
                              setSortDir('desc');
                            }
                          }}
                        >
                          <div className="flex items-center gap-1">
                            Issue Date
                            {sortField === 'issue_date' && (
                              sortDir === 'desc' ? <CaretDown size={14} /> : <CaretUp size={14} />
                            )}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Effective Date
                        </th>
                        <th className="px-4 py-3 text-center text-xs uppercase tracking-wider text-text-tertiary font-semibold">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredLaws.map((law, index) => (
                        <motion.tr
                          key={law.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.05 }}
                          className="hover:bg-background-hover transition-colors"
                          data-testid={`draft-law-row-${index}`}
                        >
                          {/* Document Name */}
                          <td className="px-4 py-4">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-text-primary">
                                  {law.document_name}
                                </span>
                                {law.is_verified && (
                                  <span className="px-1.5 py-0.5 bg-green-900/30 text-green-400 text-xs rounded border border-green-700/50">
                                    Verified
                                  </span>
                                )}
                                {law.source === 'Master Library - Auto Sync' && (
                                  <span className="px-1.5 py-0.5 bg-gold/20 text-gold text-xs rounded border border-gold/50">
                                    Master
                                  </span>
                                )}
                              </div>
                              {law.overview && law.overview !== 'Pending AI analysis...' && (
                                <div className="text-xs text-text-secondary mt-1 line-clamp-2 max-w-xs">
                                  {law.overview}
                                </div>
                              )}
                              {/* PDF Download/Upload */}
                              {law.pdf_filename ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <button
                                    onClick={() => handleViewPdf(law.id, law.pdf_original_name)}
                                    className="inline-flex items-center gap-1 text-xs text-gold hover:underline"
                                  >
                                    <FilePdf size={14} weight="fill" className="text-red-500" />
                                    View PDF
                                  </button>
                                  <span className="text-xs text-text-tertiary">
                                    ({law.pdf_size_kb}KB)
                                  </span>
                                </div>
                              ) : law.download_url ? (
                                <a
                                  href={law.download_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-gold hover:underline mt-1"
                                >
                                  <LinkIcon size={12} />
                                  External Link
                                </a>
                              ) : (
                                <label className="inline-flex items-center gap-1 text-xs text-text-tertiary hover:text-gold cursor-pointer mt-1">
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => handleUploadPdf(law.id, e.target.files[0])}
                                  />
                                  <UploadSimple size={12} />
                                  Upload PDF
                                </label>
                              )}
                            </div>
                          </td>
                          {/* Document Type */}
                          <td className="px-4 py-4">
                            <span className="px-2 py-1 bg-background rounded text-xs text-text-primary">
                              {law.document_type || 'N/A'}
                            </span>
                          </td>
                          {/* Regulator */}
                          <td className="px-4 py-4">
                            <span className="text-sm text-text-primary">
                              {law.issued_by || 'N/A'}
                            </span>
                          </td>
                          {/* Theme */}
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1.5">
                              <Tag size={14} className="text-gold" />
                              <span className="text-sm text-text-primary">{law.theme}</span>
                            </div>
                          </td>
                          {/* Status */}
                          <td className="px-4 py-4">
                            <StatusBadge status={law.status} />
                            {law.deadline && (
                              <div className="text-xs text-text-tertiary mt-1 flex items-center gap-1">
                                <Clock size={12} />
                                Due: {new Date(law.deadline).toLocaleDateString()}
                              </div>
                            )}
                          </td>
                          {/* Issue Date */}
                          <td className="px-4 py-4">
                            <span className="text-sm text-text-primary">
                              {law.publication_date ? new Date(law.publication_date).toLocaleDateString() : 'N/A'}
                            </span>
                          </td>
                          {/* Effective Date */}
                          <td className="px-4 py-4">
                            <span className="text-sm text-text-primary">
                              {law.effective_date ? new Date(law.effective_date).toLocaleDateString() : 'N/A'}
                            </span>
                          </td>
                          {/* Actions */}
                          <td className="px-4 py-4">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedLaw(law);
                                  setShowVersionModal(true);
                                }}
                                className="p-1.5 text-text-tertiary hover:text-gold transition-colors"
                                title="View History"
                              >
                                <ClockCounterClockwise size={16} />
                              </button>
                              {/* PDF Upload Button */}
                              {!law.pdf_filename && (
                                <label className="p-1.5 text-text-tertiary hover:text-gold transition-colors cursor-pointer" title="Upload PDF">
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => handleUploadPdf(law.id, e.target.files[0])}
                                  />
                                  <FilePdf size={16} />
                                </label>
                              )}
                              <button
                                onClick={() => handleDeleteLaw(law.id)}
                                className="p-1.5 text-text-tertiary hover:text-risk-high-text transition-colors"
                                title="Delete"
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-16 bg-background-surface border border-border rounded-sm">
                  <FileText size={48} className="mx-auto mb-4 text-gold" weight="light" />
                  <h3 className="text-lg font-heading font-medium text-text-primary mb-2">
                    No Documents Found
                  </h3>
                  <p className="text-sm text-text-secondary mb-6">
                    {searchQuery || statusFilter !== 'all' || themeFilter !== 'all'
                      ? 'Try adjusting your filters'
                      : 'Start building your regulatory library by scanning regulators or adding documents manually'}
                  </p>
                  {!searchQuery && statusFilter === 'all' && themeFilter === 'all' && (
                    <div className="flex items-center justify-center gap-3">
                      <button
                        onClick={() => setShowScanModal(true)}
                        className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2"
                      >
                        <Scan size={16} />
                        Scan Regulators
                      </button>
                      <button
                        onClick={() => setShowAddLawModal(true)}
                        className="px-4 py-2 bg-background border border-border text-text-primary rounded-sm hover:border-gold transition-all"
                      >
                        Add Document
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            /* Preparation Workspace Tab */
            <>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-heading font-medium text-text-primary">
                    Preparation Documents
                  </h2>
                  <p className="text-sm text-text-secondary mt-1">
                    Working documents and materials to prepare for upcoming regulatory changes
                  </p>
                </div>
                <button
                  onClick={() => setShowAddDocModal(true)}
                  className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all flex items-center gap-2"
                  data-testid="add-prep-doc-btn"
                >
                  <UploadSimple size={16} />
                  Add Document
                </button>
              </div>

              {prepDocuments.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {prepDocuments.map((doc, index) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="p-4 bg-background-surface border border-border rounded-sm hover:border-gold transition-all"
                      data-testid={`prep-doc-${index}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 bg-gold/10 rounded-sm flex items-center justify-center">
                          <FileText size={20} className="text-gold" />
                        </div>
                        <button
                          onClick={() => handleDeleteDoc(doc.id)}
                          className="p-1 text-text-tertiary hover:text-risk-high-text transition-colors"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                      <h3 className="text-sm font-medium text-text-primary mb-1">
                        {doc.document_name}
                      </h3>
                      <p className="text-xs text-text-tertiary mb-2">
                        {doc.document_type}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-text-tertiary">
                        <Tag size={12} className="text-gold" />
                        {doc.theme}
                      </div>
                      {doc.related_draft_law_id && (
                        <div className="mt-2 pt-2 border-t border-border">
                          <p className="text-xs text-text-secondary">
                            Linked to: {draftLaws.find(l => l.id === doc.related_draft_law_id)?.document_name || 'Unknown'}
                          </p>
                        </div>
                      )}
                      {doc.file_url && (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center gap-1 text-xs text-gold hover:underline"
                        >
                          <Eye size={12} />
                          View File
                        </a>
                      )}
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-background-surface border border-border rounded-sm">
                  <Files size={48} className="mx-auto mb-4 text-gold" weight="light" />
                  <h3 className="text-lg font-heading font-medium text-text-primary mb-2">
                    No Preparation Documents
                  </h3>
                  <p className="text-sm text-text-secondary mb-6">
                    Start adding working documents to prepare for regulatory changes
                  </p>
                  <button
                    onClick={() => setShowAddDocModal(true)}
                    className="px-4 py-2 bg-gold hover:bg-gold-hover text-gold-foreground rounded-sm transition-all"
                  >
                    Add Document
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <AddDraftLawModal
        isOpen={showAddLawModal}
        onClose={() => setShowAddLawModal(false)}
        onAdd={(newLaw) => setDraftLaws([newLaw, ...draftLaws])}
      />
      <AddDocumentModal
        isOpen={showAddDocModal}
        onClose={() => setShowAddDocModal(false)}
        onAdd={(newDoc) => setPrepDocuments([newDoc, ...prepDocuments])}
        draftLaws={draftLaws}
      />
      <VersionHistoryModal
        isOpen={showVersionModal}
        onClose={() => setShowVersionModal(false)}
        draftLaw={selectedLaw}
      />
      <ScanDraftLawsModal
        isOpen={showScanModal}
        onClose={() => setShowScanModal(false)}
        onImport={fetchData}
      />
      <ScanScheduleModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onSave={fetchData}
      />
      <ExcelUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={fetchData}
      />
    </div>
  );
};

export default DraftLawsPage;
